// ============================================================================
// I6 (creative-engine restructure, 2026-08-18) — the ONE shared start-flow for
// creative sessions, called from all three entry points that previously each
// carried their own copy of this branch:
//
//   - CreativeEngineDropdown  (frontend/src/App.js)
//   - CreativeActions         (frontend/src/shell/routes/workstation/CreativeActions.jsx,
//                               via its local api.js, which re-exports this module)
//   - CreativeHub             (frontend/src/components/CreativeHub.jsx) — now
//                              on startAutonomousCreativeSession, see below
//
// All three reached the same two backend endpoints with the REACT_APP_CCS_ENABLED
// cutover branch duplicated three times. workstation/api.js's own comment
// documented WHY it duplicated rather than imported: "App.js is frozen
// tonight... and the component is not exported." That constraint was scoped to
// one night's dispatch, not permanent — this dispatch (I6) is exactly the
// "reach in and consolidate" follow-up it was waiting on.
//
// CORRECTION, 2026-09-02 (lane CREATIVE-AUTONOMOUS-ROUTING)
// ---------------------------------------------------------
// I6 also read CreativeHub's missing CCS branch as drift and gave it one. That
// was wrong, and it cost the Autonomous Creative path. CreativeHub is the
// autonomous half of the /creative chooser: `CreativeLanding` in App.js renders
// it for `mode === "autonomous"`, and renders CustomCreativeSession for
// `mode === "custom"`. Routing the Hub's new sessions through the flag sent
// them to /creative?session=<id>, which CreativeLanding resolves straight back
// to CustomCreativeSession — so the Autonomous card landed in the Custom
// session and the guided workflow became unreachable from the chooser.
//
// The Hub was the one caller correctly WITHOUT a flag check. It now calls
// `startAutonomousCreativeSession`, which never consults the flag. The
// consolidation still stands for the two callers it was actually right for:
// there is one copy of the cutover branch, and it lives here.
//
// No backend endpoint changes, then or now. Same two calls as before:
//   POST /api/creative/sessions   -> /creative-engine/:offerId?session=<id>&new=1
//                                     (always, for the autonomous entry point)
//   POST /api/ccs/sessions        -> /creative?session=<id>          (when the
//                                     REACT_APP_CCS_ENABLED cutover flag is on)
// ============================================================================
import axios from 'axios';

import { V2_ENABLED } from '../shell/flag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** True when the shared-custom-creative-session cutover is on. */
export const ccsEnabled = () =>
  String(process.env.REACT_APP_CCS_ENABLED).toLowerCase() === 'true';

/**
 * The session library for one offer. `creative_engine.py:1483`
 *
 * Returns `{ ok, data, error, status }` — the same never-throws contract
 * `frontend/src/shell/routes/workstation/api.js`'s other endpoints use (its
 * `useEndpoint` hook renders idle/loading/ready/error from exactly this
 * shape), so this can be called directly wherever that contract is expected.
 */
export async function listCreativeSessions(offerId) {
  try {
    const res = await axios.get(`${API}/creative/sessions`, {
      params: { offer_id: offerId },
      withCredentials: true,
    });
    return { ok: true, data: res.data, error: null, status: null };
  } catch (e) {
    return {
      ok: false,
      data: null,
      status: e?.response?.status ?? null,
      error: e?.response?.data?.detail || e?.message || 'request failed',
    };
  }
}

/**
 * WHERE A CREATIVE SESSION OPENS — the one decision, in one place.
 *
 * THE DEFECT THIS FIXES, 2026-09-09. Every entry point that created a session
 * sent the operator to a v1 route, so **the new wizard had no front door**: it
 * was reachable only by resuming a session that already existed, never by
 * starting one. Traced before changing anything, and all three led to v1:
 *
 *   App.js dropdown      -> `startCreativeSession`; the CCS cutover is ON in
 *                           production (read from Render, 2026-09-09), so it
 *                           creates a CUSTOM session and lands on v1's
 *                           `/creative?session=ccs_…`. So Custom had the same
 *                           problem, from this entry point — the answer to
 *                           "is this Autonomous-only" is no.
 *   CreativeHub          -> `startAutonomousCreativeSession` -> v1's engine.
 *   CreativeActions      -> autonomous went to v1, LEAVING v2 to do it; only
 *                           its Custom button redirected into v2, locally.
 *
 * v2 dispatches on the id prefix (`cs_` autonomous, `ccs_` custom) and reads
 * `new=1`, so one route serves both — see `AdsCreative.jsx`.
 *
 * FLAG-AWARE ON PURPOSE. `V2_ENABLED` defaults OFF, and when it is off App.js
 * does not register `/v2/*` at all — routing there would be a 404 rather than a
 * new interface. Live value is `true` (Render, 2026-09-09), but this function
 * must be correct without depending on that, so it falls back to the v1 path it
 * has always returned.
 */
export const creativeSurfacePath = (offerId, sessionId, { isNew = false, isCustom } = {}) => {
  // The CALLER knows which kind it just created; `sessionPath` (opening an
  // existing one) does not, and falls back to the id prefix v2 already
  // dispatches on. Explicit beats sniffing wherever explicit is available —
  // caught by test: with v2 off, a custom session whose id did not begin
  // `ccs_` would have been sent to the AUTONOMOUS v1 route.
  const custom = isCustom !== undefined
    ? isCustom
    : String(sessionId || '').startsWith('ccs_');
  if (V2_ENABLED) {
    const params = new URLSearchParams();
    if (offerId) params.set('offer', offerId);
    params.set('session', sessionId);
    if (isNew) params.set('new', '1');
    return `/v2/workstation/creative?${params.toString()}`;
  }
  if (custom) return `/creative?session=${sessionId}`;
  return `/creative-engine/${offerId}?session=${sessionId}${isNew ? '&new=1' : ''}`;
};

/** Where an existing session opens. */
export const sessionPath = (offerId, sessionId) =>
  creativeSurfacePath(offerId, sessionId);

/**
 * Start an AUTONOMOUS creative session — the guided workflow (psychology →
 * angles → hooks → scripts → production) that lives at
 * `/creative-engine/:offerId`.
 *
 * This one does NOT consult the cutover flag, and that is the whole point of
 * it existing separately. `REACT_APP_CCS_ENABLED` selects which session an
 * operator gets when the two flows are interchangeable for that entry point.
 * They are not interchangeable at the Creative Hub: the Hub *is* the
 * autonomous half of the `/creative` chooser (`App.js`, `CreativeLanding` —
 * `mode === "autonomous"` renders it), so sending its sessions to the custom
 * surface makes the Autonomous card land in the Custom session and leaves the
 * guided workflow unreachable from the chooser.
 *
 * That is exactly what happened. I6 (below) folded the Hub's call into the
 * flag-branching `startCreativeSession`, reading the Hub's lack of a flag
 * check as drift. It was not drift — the Hub was the one caller correctly
 * without one. This restores the pre-I6 behaviour for that caller only, and
 * is identical to `startCreativeSession` whenever the flag is off.
 *
 * `&new=1` is preserved from the original: without it the engine's step
 * components fall through to the offer's last-session data (psychology_map,
 * selected_angle_id, hooks, scripts) instead of starting blank.
 */
export async function startAutonomousCreativeSession(offerId) {
  try {
    const res = await axios.post(`${API}/creative/sessions`, { offer_id: offerId });
    const sessionId = res.data.session_id;
    return { ok: true, error: null, sessionId,
             path: creativeSurfacePath(offerId, sessionId, { isNew: true, isCustom: false }) };
  } catch (e) {
    return { ok: false, error: e.response?.data?.detail || e.message || 'Failed to create session', sessionId: null, path: null };
  }
}

/**
 * Start a new creative session for `offerId`, honoring the CCS cutover flag.
 * Returns `{ ok, path, sessionId, error }` — never throws. The caller
 * navigates; this module stays free of routing so it works the same from a
 * dropdown or a workstation panel.
 *
 * Callers: `CreativeEngineDropdown` (`App.js`) and `CreativeActions`
 * (`shell/routes/workstation/`). Both are offer-level "New Creative Session"
 * buttons, for which the cutover is meant to be transparent — they keep this
 * behaviour unchanged. The Creative Hub is NOT a caller of this any more; see
 * `startAutonomousCreativeSession` above for why.
 */
export async function startCreativeSession(offerId) {
  try {
    if (ccsEnabled()) {
      // Cutover: creates a Shared Custom Creative Session for this offer and
      // lands directly in it — the operator never sees /ccs or the legacy
      // engine as a separate surface.
      const res = await axios.post(`${API}/ccs/sessions`, { offer_id: offerId });
      const sid = res.data.session_id;
      return { ok: true, error: null, sessionId: sid,
               path: creativeSurfacePath(offerId, sid, { isNew: true, isCustom: true }) };
    }
    return startAutonomousCreativeSession(offerId);
  } catch (e) {
    return { ok: false, error: e.response?.data?.detail || e.message || 'Failed to create session', sessionId: null, path: null };
  }
}
